import React, { useState } from 'react';
import styled from 'styled-components';

function EditReasonPopup({ onSubmit, onCancel }) {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    const validate = (value) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return 'Please enter a reason for the changes';
        }
        if (trimmedValue.length < 3) {
            return 'Reason must be at least 3 characters long';
        }
        return '';
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setReason(value);
        if (error) {
            setError('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationError = validate(reason);
        if (validationError) {
            setError(validationError);
            return;
        }
        onSubmit(reason.trim());
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit(e);
        }
    };

    return (
        <ModalWrapper onClick={(e) => e.target === e.currentTarget && onCancel()}>
            <ModalContent>
                <Title>Why are you making these changes?</Title>
                <TextArea
                    value={reason}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Describe your changes..."
                    autoFocus
                    $error={error}
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <ButtonGroup>
                    <CancelButton type="button" onClick={onCancel}>
                        Cancel
                    </CancelButton>
                    <SubmitButton
                        type="button"
                        onClick={handleSubmit}
                        disabled={!reason.trim()}
                    >
                        Submit
                    </SubmitButton>
                </ButtonGroup>
            </ModalContent>
        </ModalWrapper>
    );
}

const ModalWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalContent = styled.div`
    background: white;
    padding: 24px;
    border-radius: 8px;
    width: 400px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
    margin: 0 0 16px 0;
    color: #333;
    font-size: 18px;
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 100px;
    margin: 8px 0;
    padding: 12px;
    border: 1px solid ${props => props.$error ? '#dc3545' : '#ddd'};
    border-radius: 4px;
    resize: vertical;
    font-size: 14px;
    line-height: 1.5;
    transition: border-color 0.2s ease;

    &:focus {
        outline: none;
        border-color: ${props => props.$error ? '#dc3545' : '#1a73e8'};
        box-shadow: 0 0 0 2px ${props => props.$error ? 'rgba(220, 53, 69, 0.25)' : 'rgba(26, 115, 232, 0.25)'};
    }

    &::placeholder {
        color: #999;
    }
`;

const ErrorMessage = styled.div`
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`;

const Button = styled.button`
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const CancelButton = styled(Button)`
    background-color: #f1f3f4;
    color: #333;
    
    &:hover:not(:disabled) {
        background-color: #e8eaed;
    }
`;

const SubmitButton = styled(Button)`
    background-color: #1a73e8;
    color: white;
    
    &:hover:not(:disabled) {
        background-color: #1557b0;
    }
    
    &:disabled {
        background-color: #ccc;
    }
`;

export default EditReasonPopup;